import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { useQuery } from 'react-query';
import { menuService } from '../../services/menuService';

// ---------------------------------------------------------------------------
// STATIC SEARCH DATA
// Built from the real content across Home, About, Contact, Services, and
// Products pages (including each product & service detail page). Each entry
// has a title, the route it should navigate to, a type/category label, and
// a "keywords" array of extra terms pulled from the page's own description
// text so a search for a related word (not just the exact title) still
// finds the right page.
// ---------------------------------------------------------------------------
const STATIC_SEARCH_DATA = [
  // ---- Core Pages ----
  { title: 'Home', path: '/', type: 'Page', keywords: ['home', 'main', 'embedded systems', 'fleet tracking'] },
  { title: 'About Us', path: '/about', type: 'Page', keywords: ['about', 'company', 'vision', 'core values', 'mission', 'who we are'] },
  { title: 'Contact Us', path: '/contact', type: 'Page', keywords: ['contact', 'reach us', 'get in touch', 'phone', 'email', 'address', 'office', 'mumbai', 'goregaon'] },
  { title: 'Services', path: '/services', type: 'Page', keywords: ['services', 'expertise', 'what we do', 'iot services'] },
  { title: 'Products', path: '/products', type: 'Page', keywords: ['products', 'iot products', 'devices', 'hardware'] },

  // ---- Services (Services.jsx + ServiceDetail.jsx) ----
  {
    title: 'Hardware Solutions',
    path: '/services/device-manufacturing',
    type: 'Service',
    keywords: [
      'device manufacturing', 'hardware engineering', 'gps tracking device',
      'e-rutu', 'iso 9001', 'fcc', 'ce', 'ptcrb', 'rohs', 'manufacturing',
      'oem', 'white label', 'ip67', 'swrt', 'sensor', 'rugged devices',
    ],
  },
  {
    title: 'Software Solutions',
    path: '/services/mobile-application',
    type: 'Service',
    keywords: [
      'software development', 'mobile application', 'ios app', 'android app',
      'driver app', 'fleet manager app', 'navigation', 'proof of delivery',
      'offline sync', 'white label app', 'mobile', 'field operations',
    ],
  },
  {
    title: 'Expert Support',
    path: '/services/support-consulting',
    type: 'Service',
    keywords: [
      'support', 'consulting', 'customer success', 'technical support',
      '24/7 support', 'training', 'implementation', 'onboarding',
      'compliance', 'haccp', 'gdp', 'dot regulations', 'help desk',
    ],
  },

  // ---- Products (Products.jsx + ProductDetail.jsx + ProductsPreview.jsx) ----
  {
    title: 'Wireless 3Phase A.C Frequency Detector',
    path: '/products/power-monitor',
    type: 'Product',
    keywords: [
      'power monitor', 'phase detector', 'ac frequency', 'voltage',
      'generator monitoring', 'phase loss', 'ble 5.0', 'din rail',
      'power supply monitoring', 'industrial power',
    ],
  },
  {
    title: 'E-Vahan BLE + 4G/2G Tracker',
    path: '/products/fleet-track',
    type: 'Product',
    keywords: [
      'fleet track', 'vehicle tracker', 'gps tracker', '4g tracker',
      'fleet management', 'ignition detection', 'geofence', 'e-vahan',
      'ble mesh', 'car tracker', 'truck tracker',
    ],
  },
  {
    title: 'Temperature & Humidity Sensor',
    path: '/products/sensor-network',
    type: 'Product',
    keywords: [
      'sensor network', 'temperature sensor', 'humidity sensor',
      'cold chain', 'warehouse monitoring', 'ble mesh', 'rht',
      'environmental monitoring', 'wireless sensor',
    ],
  },
  {
    title: '4G/2G Tracker with RHT',
    path: '/products/smart-track',
    type: 'Product',
    keywords: [
      'smart track', 'rht tracker', 'door sensor', 'temperature tracking',
      'asset tracking', 'logistics tracker', 'cargo monitoring', 'e-rutu',
    ],
  },
  {
    title: 'IoT Connectivity Platform',
    path: '/products/ble-network',
    type: 'Product',
    keywords: [
      'ble network', 'iot platform', 'bluetooth mesh', 'mqtt',
      'gateway', 'connectivity platform', 'iot connectivity', 'nodes',
    ],
  },
  {
    title: 'Smart Protection System',
    path: '/products/asset-security',
    type: 'Product',
    keywords: [
      'asset security', 'protection system', 'motion sensor', 'geofence',
      'security alert', 'asset monitoring', 'anti-theft',
    ],
  },
  {
    title: 'BLE Smart ID Card',
    path: '/products/ble-id-card',
    type: 'Product',
    keywords: [
      'ble id card', 'smart id', 'access control', 'employee id',
      'attendance', 'visitor management', 'indoor positioning',
      'contactless authentication', 'cr2032',
    ],
  },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const searchWrapRef = useRef(null);

  const { data: menus } = useQuery('menus', menuService.getAll);
  const menusList = Array.isArray(menus) ? menus : [];

  const getMenuItems = (category) => {
    const found = menusList.find((m) => m && m.category === category);
    return Array.isArray(found?.items) ? found.items : [];
  };

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'ABOUT', path: '/about' },
    { name: 'SERVICES', path: '/services', dropdown: getMenuItems('services'), type: 'service' },
    { name: 'PRODUCTS', path: '/products', dropdown: getMenuItems('products'), type: 'product' },
    { name: 'TRACKING', path: 'http://freetrack.in', external: true },
  ];

  // Merge static site-wide keyword data with any dynamic CMS menu items
  // (in case services/products get added/edited from a backend later).
  const searchIndex = useMemo(() => {
    const dynamicServices = getMenuItems('services').map((item) => ({
      title: item.title,
      path: `/services/${item.slug}`,
      type: 'Service',
      keywords: [],
    }));

    const dynamicProducts = getMenuItems('products').map((item) => ({
      title: item.title,
      path: `/products/${item.slug}`,
      type: 'Product',
      keywords: [],
    }));

    // De-dupe by path so we don't show the same item twice if the CMS
    // already lists something already covered by STATIC_SEARCH_DATA.
    const combined = [...STATIC_SEARCH_DATA, ...dynamicServices, ...dynamicProducts];
    const seenPaths = new Set();
    return combined.filter((entry) => {
      if (seenPaths.has(entry.path)) return false;
      seenPaths.add(entry.path);
      return true;
    });
  }, [menusList]);

  // Filter search index whenever the query changes (debounced).
  // Matches against title AND keywords, so typing "temperature" or
  // "fleet" or "gps" surfaces relevant products/services, not just
  // exact title matches.
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      const q = searchQuery.trim().toLowerCase();

      const scored = searchIndex
        .map((entry) => {
          const titleMatch = entry.title.toLowerCase().includes(q);
          const keywordMatch = (entry.keywords || []).some((k) => k.toLowerCase().includes(q));
          if (!titleMatch && !keywordMatch) return null;
          // Rank exact/title matches above keyword-only matches
          const score = titleMatch ? 2 : 1;
          return { ...entry, score };
        })
        .filter(Boolean)
        .sort((a, b) => b.score - a.score);

      setSearchResults(scored.slice(0, 8)); // cap results shown
    }, 200); // debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery, searchIndex]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      navigate(searchResults[0].path);
      closeSearch();
    }
  };

  const handleResultClick = (path) => {
    navigate(path);
    closeSearch();
  };

  const openSearch = () => {
    setIsSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (!isSearchOpen) return;
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) closeSearch();
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isSearchOpen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && isSearchOpen) closeSearch();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isSearchOpen]);

  const Underline = () => (
    <span className="absolute left-0 bottom-[1px] h-[2px] bg-black w-0 group-hover:w-full transition-all duration-300" />
  );

  const linkCls = () =>
    `relative group inline-flex items-center h-16
     text-[16px] font-semibold uppercase
     tracking-[0.06em] leading-[1.2]
     text-[#222] hover:text-black
     no-underline`;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
      <div className="max-w-full mx-auto px-6 lg:px-12">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center h-16 space-x-3 flex-shrink-0">
            <img
              src="/assets/images/siwilogo.png"
              alt="Srishti Wireless Solution"
              className="h-10 w-auto object-contain"
            />
            <span className="hidden lg:inline-flex items-center h-16 text-[18px] font-semibold uppercase tracking-[0.06em] leading-none text-black">
              SRISHTI WIRELESS SOLUTION
            </span>
          </Link>

          {/* Center: Nav Links */}
          <div className="hidden lg:flex items-center justify-center absolute left-1/2 transform -translate-x-1/2">
            <div className="flex items-center space-x-10">
              {navLinks.map((link) => {
                const hasDropdown = Array.isArray(link.dropdown) && link.dropdown.length > 0;
                return (
                  <div
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => hasDropdown && setActiveDropdown(link.name)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    {link.external ? (
                      <a href={link.path} target="_blank" rel="noopener noreferrer" className={linkCls()}>
                        {link.name}
                        <Underline />
                      </a>
                    ) : (
                      <Link to={link.path} className={linkCls()}>
                        {link.name}
                        <Underline />
                      </Link>
                    )}

                    {hasDropdown && activeDropdown === link.name && (
                      <div className="absolute top-full left-0 mt-2 w-60 bg-white shadow-lg border border-gray-200">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item._id || item.slug}
                            to={`/${link.type}s/${item.slug}`}
                            className="block px-4 py-3 text-sm text-gray-800 hover:bg-gray-50 hover:text-black transition-colors border-b border-gray-100 last:border-0"
                          >
                            {item.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Search + Contact */}
          <div className="hidden lg:flex items-center space-x-4 flex-shrink-0 h-full">
            <div ref={searchWrapRef} className="relative flex items-center h-full">
              <div className={`transition-all duration-300 ease-out ${isSearchOpen ? 'w-[260px]' : 'w-10'}`}>
                <div className="flex items-center h-10 border border-gray-300 bg-white">
                  <button
                    type="button"
                    onClick={() => {
                      if (!isSearchOpen) openSearch();
                      else searchInputRef.current?.focus();
                    }}
                    className="w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-50 transition flex-shrink-0"
                    aria-label="Open search"
                  >
                    <Search size={16} className="text-gray-700" strokeWidth={2} />
                  </button>

                  <form onSubmit={handleSearchSubmit} className="flex-1">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search Here"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={`h-10 w-full bg-transparent px-2 text-[18px] text-gray-700 placeholder-gray-500
                                  outline-none border-none focus:outline-none focus:ring-0 ${
                                    isSearchOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                  } transition-opacity duration-200`}
                    />
                  </form>

                  <button
                    type="button"
                    onClick={closeSearch}
                    className={`w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-50 transition flex-shrink-0 ${
                      isSearchOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    } transition-opacity duration-200`}
                    aria-label="Close search"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* Search results dropdown */}
              {isSearchOpen && searchQuery.trim() && (
                <div className="absolute top-full left-0 mt-2 w-[320px] bg-white shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50">
                  {searchResults.length > 0 ? (
                    searchResults.map((result, idx) => (
                      <button
                        key={`${result.path}-${idx}`}
                        onClick={() => handleResultClick(result.path)}
                        className="w-full text-left px-4 py-3 text-sm text-gray-800 hover:bg-gray-50 hover:text-black transition-colors border-b border-gray-100 last:border-0 flex items-center justify-between"
                      >
                        <span>{result.title}</span>
                        <span className="text-xs text-gray-400 uppercase ml-2 flex-shrink-0">{result.type}</span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500">No results found</div>
                  )}
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className="inline-flex items-center justify-center h-10 px-6
                        border border-gray-300 bg-transparent text-black
                        text-[16px] font-semibold uppercase
                        tracking-[0.06em] leading-[1.2]
                        hover:bg-black hover:text-white
                        transition-all duration-300"
            >
              CONTACT US
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden p-2 text-gray-900 hover:bg-gray-100" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          {/* Mobile Search */}
          <div className="px-4 pt-4">
            <div className="flex items-center h-11 border border-gray-300 bg-white px-3">
              <Search size={16} className="text-gray-500 flex-shrink-0" />
              <input
                type="text"
                placeholder="Search Here"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-full w-full bg-transparent px-2 text-[15px] text-gray-700 placeholder-gray-500 outline-none border-none"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} aria-label="Clear search">
                  <X size={16} className="text-gray-500" />
                </button>
              )}
            </div>

            {searchQuery.trim() && (
              <div className="mt-2 border border-gray-200 max-h-72 overflow-y-auto">
                {searchResults.length > 0 ? (
                  searchResults.map((result, idx) => (
                    <button
                      key={`${result.path}-${idx}`}
                      onClick={() => {
                        navigate(result.path);
                        setSearchQuery('');
                        setIsOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-sm text-gray-800 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between"
                    >
                      <span>{result.title}</span>
                      <span className="text-xs text-gray-400 uppercase ml-2 flex-shrink-0">{result.type}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500">No results found</div>
                )}
              </div>
            )}
          </div>

          <div className="px-4 py-2 space-y-1">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.external ? (
                  <a
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block py-3 text-[15px] font-bold uppercase tracking-[0.10em] text-gray-900"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    to={link.path}
                    className="block py-3 text-[15px] font-bold uppercase tracking-[0.10em] text-gray-900"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}

            <div className="pt-4 pb-2">
              <Link
                to="/contact"
                className="block w-full text-center px-6 py-3 bg-black text-white text-[15px] font-bold uppercase tracking-[0.10em]"
                onClick={() => setIsOpen(false)}
              >
                CONTACT US
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;